/**
 * Pure helpers that read one claim - ``exp`` - from an ID token already held in memory,
 * so the console can choose between a plain and a forced silent renewal
 * (specs/20260916-cortex-console-session-keep-alive design.md section 3).
 *
 * Nothing here validates a token; the console API does that. These only answer "is
 * this token about to stop being accepted", and they never throw on garbage input.
 */

function decodeBase64Url(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
    return atob(padded + pad);
  } catch {
    return null;
  }
}

/** The token's ``exp`` claim in epoch milliseconds, or ``null`` when it cannot be read. */
export function decodeExpMs(jwt: string | null | undefined): number | null {
  if (!jwt) {
    return null;
  }
  const parts = jwt.split(".");
  if (parts.length < 2) {
    return null;
  }
  const payload = decodeBase64Url(parts[1]);
  if (payload === null) {
    return null;
  }
  try {
    const claims = JSON.parse(payload) as { exp?: unknown };
    return typeof claims.exp === "number" && Number.isFinite(claims.exp)
      ? claims.exp * 1000
      : null;
  } catch {
    return null;
  }
}

/**
 * ``true`` when the token is absent, unreadable, or within ``skewMs`` of its expiry.
 * An unreadable token is treated as stale on purpose: forcing one renewal is cheaper
 * than sending the API something it will refuse.
 */
export function isIdTokenStale(
  jwt: string | null | undefined,
  nowMs: number,
  skewMs: number,
): boolean {
  const expMs = decodeExpMs(jwt);
  if (expMs === null) {
    return true;
  }
  return expMs - skewMs <= nowMs;
}

/** ``true`` only when the token can be read and its ``exp`` is still in the future. */
export function isIdTokenStillValid(jwt: string | null | undefined, nowMs: number): boolean {
  const expMs = decodeExpMs(jwt);
  return expMs !== null && expMs > nowMs;
}
